import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Board } from '../interfaces/board';

interface BoardPermissions {
  isAdmin: boolean;
  isMember: boolean;
  isObserver: boolean;
  canEdit: boolean;
  canDelete: boolean;
  canManageUsers: boolean;
  canCreateCards: boolean;
  canMoveCards: boolean;
  accessLevel: string;
}

/**
 * Hook para verificar as permissões do usuário em relação a um board específico
 * 
 * @param board O board atual
 * @param projectPermissions Permissões do usuário no projeto (opcional)
 * @returns Objeto com as permissões do usuário no board
 */
export const useBoardPermissions = (board: Board | null, projectPermissions?: any): BoardPermissions => {
  const { user } = useAuth();
  
  const [permissions, setPermissions] = useState<BoardPermissions>({
    isAdmin: false,
    isMember: false,
    isObserver: false,
    canEdit: false,
    canDelete: false,
    canManageUsers: false,
    canCreateCards: false,
    canMoveCards: false,
    accessLevel: 'Observer'
  });

  useEffect(() => {
    if (!user || !board) {
      setPermissions({
        isAdmin: false,
        isMember: false,
        isObserver: false,
        canEdit: false,
        canDelete: false,
        canManageUsers: false,
        canCreateCards: false,
        canMoveCards: false,
        accessLevel: 'Observer'
      });
      return;
    }

    // Verificar se é administrador do sistema
    const isSystemAdmin = (
      user.role?.toLowerCase() === 'admin' || 
      user.role?.toLowerCase() === 'administrator' || 
      user.role?.toLowerCase() === 'developer' || 
      user.role?.toLowerCase() === 'dev'
    );
    
    // Se tem permissões do projeto, usar elas como base
    let effectiveAccessLevel = 'Observer';
    
    if (projectPermissions) {
      // Se é admin do projeto, é admin de todos os boards
      if (projectPermissions.isAdmin) {
        effectiveAccessLevel = 'Administrator';
      } else if (!projectPermissions.isObserver) {
        // Se não é observador, é membro
        effectiveAccessLevel = 'Member';
      } else {
        effectiveAccessLevel = 'Observer';
      }
    }
    
    // Se o usuário tem acesso direto ao board, sobrescrever com a permissão específica
    if (board.hasDirectAccess && board.userAccessLevel) {
      effectiveAccessLevel = board.userAccessLevel;
    }
    
    // Sistema admin sempre tem acesso de administrador
    if (isSystemAdmin) {
      effectiveAccessLevel = 'Administrator';
    }
    
    // Determinar permissões baseadas no nível de acesso efetivo
    const isAdmin = effectiveAccessLevel === 'Administrator';
    const isMember = effectiveAccessLevel === 'Member';
    const isObserver = effectiveAccessLevel === 'Observer';
    
    setPermissions({
      isAdmin,
      isMember,
      isObserver,
      canEdit: isAdmin || isMember,
      canDelete: isAdmin,
      canManageUsers: isAdmin,
      canCreateCards: isAdmin || isMember,
      canMoveCards: isAdmin || isMember,
      accessLevel: effectiveAccessLevel
    });
  }, [user, board, projectPermissions]);

  return permissions;
};
